import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Question as QuestionIcon, ChatCircleText, Textbox, GraduationCap, BookOpen,
  MicrophoneStage, Stack, Plus, ArrowLeft, CheckCircle, CircleDashed,
  Trash, PaperPlaneTilt, Spinner, Trophy, PencilSimpleLine, FloppyDisk, X, ArrowBendUpLeft
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1'];
const CATEGORIES = [
  { value: 'platform', label: 'Plataforma', icon: QuestionIcon, color: '#0F766E' },
  { value: 'course', label: 'Curso', icon: Stack, color: '#6D28D9' },
  { value: 'communication', label: 'Comunicación', icon: ChatCircleText, color: '#AB1C24' },
  { value: 'vocabulary', label: 'Vocabulario', icon: Textbox, color: '#5B8C9A' },
  { value: 'grammar', label: 'Gramática', icon: GraduationCap, color: '#8B5A3C' },
  { value: 'reading', label: 'Comprensión lectora', icon: BookOpen, color: '#2F6B50' },
  { value: 'pronunciation', label: 'Pronunciación', icon: MicrophoneStage, color: '#D97706' },
];
const catByVal = Object.fromEntries(CATEGORIES.map(c => [c.value, c]));

// Small multi-level selector (keeps at least one level selected)
const LevelPills = ({ value = ['A2'], onChange, testIdPrefix = 'q-level' }) => {
  const toggle = (lvl) => {
    const has = value.includes(lvl);
    let next = has ? value.filter(l => l !== lvl) : [...value, lvl];
    if (next.length === 0) next = [lvl];
    next = LEVELS.filter(l => next.includes(l));
    onChange(next);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-pills`}>
      {LEVELS.map(l => {
        const active = value.includes(l);
        return (
          <button
            key={l} type="button" onClick={() => toggle(l)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${active
              ? 'bg-[#AB1C24] text-white border-[#AB1C24]'
              : 'bg-white text-[#78716C] border-stone-300 hover:border-[#AB1C24] hover:text-[#AB1C24]'}`}
            data-testid={`${testIdPrefix}-pill-${l}`}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
};

export default function DudasPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const isDe = lang === 'de';
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState(user?.spanish_level || 'all');
  const [catFilter, setCatFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [newQ, setNewQ] = useState({
    title: '', content: '',
    levels: [user?.spanish_level || 'A2'],
    category: 'communication',
  });

  const load = async () => {
    try {
      const params = {};
      if (levelFilter !== 'all') params.level = levelFilter;
      if (catFilter !== 'all') params.category = catFilter;
      const res = await axios.get(`${API}/questions`, { params, withCredentials: true });
      setQuestions(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [levelFilter, catFilter]);

  const submit = async () => {
    if (!newQ.title.trim() || newQ.title.length < 3) return toast.error('Título demasiado corto');
    try {
      await axios.post(`${API}/questions`, newQ, { withCredentials: true });
      toast.success('Duda publicada');
      setShowForm(false);
      setNewQ({ title: '', content: '', levels: [user?.spanish_level || 'A2'], category: 'communication' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al publicar');
    }
  };

  return (
    <div className="space-y-6" data-testid="dudas-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524] flex items-center gap-2">
            <QuestionIcon size={28} weight="duotone" className="text-[#AB1C24]" /> {isDe ? 'Fragen' : 'Dudas'}
          </h1>
          <p className="text-[#57534E] mt-1 text-sm">{isDe ? 'Frag Alberto und andere Schüler — du erhältst schnelle Antworten.' : 'Pregunta lo que no entiendes. Te responden Alberto y otros alumnos.'}</p>
        </div>
        <Button onClick={() => setShowForm(s => !s)} className="bg-[#AB1C24] hover:bg-[#8A151C] gap-2" data-testid="dudas-new-btn">
          <Plus size={16} /> Nueva duda
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Nivel:</span>
        {['all', ...LEVELS].map(l => (
          <button
            key={l}
            onClick={() => setLevelFilter(l)}
            className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${
              levelFilter === l ? 'bg-[#AB1C24] text-white border-[#AB1C24]' : 'bg-white text-[#57534E] border-[#D6D3D1] hover:border-[#AB1C24]'
            }`}
            data-testid={`filter-lvl-${l}`}
          >
            {l === 'all' ? 'Todos' : l}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-[#57534E] uppercase tracking-wider">Categoría:</span>
        <button onClick={() => setCatFilter('all')} className={`px-3 py-1 text-xs font-semibold rounded-full border ${catFilter === 'all' ? 'bg-[#292524] text-white border-[#292524]' : 'bg-white text-[#57534E] border-[#D6D3D1]'}`}>Todas</button>
        {CATEGORIES.map(c => {
          const Icon = c.icon;
          const on = catFilter === c.value;
          return (
            <button
              key={c.value}
              onClick={() => setCatFilter(c.value)}
              className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${on ? 'text-white' : 'text-[#57534E] bg-white border-[#D6D3D1]'}`}
              style={on ? { backgroundColor: c.color, borderColor: c.color } : {}}
              data-testid={`filter-cat-${c.value}`}
            >
              <Icon size={13} weight={on ? 'fill' : 'regular'} />
              {c.label}
            </button>
          );
        })}
      </div>

      {/* New question form */}
      {showForm && (
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-5 space-y-3" data-testid="dudas-form">
          <Input placeholder="Título corto de tu duda" value={newQ.title} onChange={e => setNewQ(p => ({...p, title: e.target.value}))} maxLength={200} data-testid="duda-title" />
          <Textarea placeholder="Explica tu duda con detalle (opcional, pero ayuda)" value={newQ.content} onChange={e => setNewQ(p => ({...p, content: e.target.value}))} maxLength={4000} rows={4} data-testid="duda-body" />
          <div>
            <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wider mb-1.5">Niveles (uno o varios)</p>
            <LevelPills value={newQ.levels} onChange={v => setNewQ(p => ({...p, levels: v}))} testIdPrefix="duda-level" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Select value={newQ.category} onValueChange={v => setNewQ(p => ({...p, category: v}))}>
              <SelectTrigger className="flex-1 min-w-[180px]" data-testid="duda-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button onClick={submit} className="bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="duda-submit">Publicar</Button>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-12"><Spinner size={32} className="animate-spin text-[#AB1C24] mx-auto" /></div>
      ) : questions.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[#D6D3D1]">
          <QuestionIcon size={48} className="mx-auto text-[#D6D3D1] mb-3" />
          <p className="text-[#78716C]">Aún no hay dudas aquí. ¡Sé el primero!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {questions.map(q => <QuestionRow key={q.question_id} q={q} onDeleted={load} />)}
        </div>
      )}
    </div>
  );
}

const QuestionRow = ({ q, onDeleted }) => {
  const { user } = useAuth();
  const cat = catByVal[q.category] || CATEGORIES[0];
  const Icon = cat.icon;
  const canDelete = user && (user.user_id === q.author_id || user.role === 'admin' || user.role === 'teacher');

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar la duda "${q.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/questions/${q.question_id}`, { withCredentials: true });
      toast.success('Duda eliminada');
      onDeleted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <Link
      to={`/dudas/${q.question_id}`}
      className="block bg-white rounded-2xl border border-[#D6D3D1] p-4 hover:border-[#AB1C24] transition-colors"
      data-testid={`question-${q.question_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${cat.color}15` }}>
          <Icon size={20} weight="duotone" style={{ color: cat.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {(q.levels && q.levels.length > 0 ? q.levels : [q.level]).map(lvl => (
              <span key={lvl} className={`level-badge ${lvl}`}>{lvl}</span>
            ))}
            <span className="text-xs font-semibold" style={{ color: cat.color }}>{cat.label}</span>
            {q.resolved && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                <CheckCircle size={12} weight="fill" /> Resuelta
              </span>
            )}
          </div>
          <h3 className="font-semibold text-[#292524] truncate">{q.title}</h3>
          {q.content && <p className="text-sm text-[#78716C] line-clamp-2 mt-0.5">{q.content}</p>}
          <p className="text-[11px] text-[#A8A29E] mt-1.5">{q.author_name} · {q.answer_count} respuesta{q.answer_count === 1 ? '' : 's'}</p>
        </div>
        {canDelete && (
          <button
            onClick={handleDelete}
            className="p-2 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
            aria-label="Eliminar duda"
            title="Eliminar duda"
            data-testid={`delete-question-${q.question_id}`}
          >
            <Trash size={16} />
          </button>
        )}
      </div>
    </Link>
  );
};

// ───── Detail page with threaded answers ─────
export function DudaDetailPage() {
  const { question_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({ category: 'communication', levels: ['A2'] });
  // Reply to specific answer + edit answer inline
  const [replyToAnswer, setReplyToAnswer] = useState(null);   // answer_id
  const [replyAnswerText, setReplyAnswerText] = useState('');
  const [editingAnswer, setEditingAnswer] = useState(null);   // answer_id
  const [editAnswerText, setEditAnswerText] = useState('');

  const load = async () => {
    try {
      const res = await axios.get(`${API}/questions/${question_id}`, { withCredentials: true });
      setQ(res.data);
    } catch (e) { toast.error('No se pudo cargar'); }
  };
  useEffect(() => { load(); }, [question_id]);

  const submit = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/questions/${question_id}/answers`, { content: reply.trim() }, { withCredentials: true });
      setReply('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al responder'); }
    finally { setSending(false); }
  };

  const submitReplyToAnswer = async (parent_answer_id) => {
    if (!replyAnswerText.trim()) return;
    try {
      await axios.post(
        `${API}/questions/${question_id}/answers`,
        { content: replyAnswerText.trim(), parent_answer_id },
        { withCredentials: true }
      );
      setReplyToAnswer(null);
      setReplyAnswerText('');
      load();
      toast.success('Respuesta publicada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const saveAnswerEdit = async (aid) => {
    if (!editAnswerText.trim()) return;
    try {
      await axios.patch(
        `${API}/questions/${question_id}/answers/${aid}`,
        { content: editAnswerText.trim() },
        { withCredentials: true }
      );
      setEditingAnswer(null);
      setEditAnswerText('');
      load();
      toast.success('Respuesta actualizada');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const toggleResolved = async () => {
    try {
      await axios.patch(`${API}/questions/${question_id}/resolve`, { resolved: !q.resolved }, { withCredentials: true });
      load();
    } catch (e) { toast.error('Error'); }
  };

  const acceptAnswer = async (aid) => {
    if (!window.confirm('¿Marcar esta respuesta como la que te resolvió la duda? El autor recibirá +10 puntos.')) return;
    try {
      const res = await axios.patch(
        `${API}/questions/${question_id}/resolve`,
        { resolved: true, accepted_answer_id: aid },
        { withCredentials: true }
      );
      if (res.data?.awarded) {
        toast.success(`Duda resuelta · +${res.data.points_awarded} puntos para el autor`);
      } else {
        toast.success('Duda resuelta');
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const deleteAnswer = async (aid) => {
    if (!window.confirm('¿Eliminar esta respuesta?')) return;
    try {
      await axios.delete(`${API}/questions/${question_id}/answers/${aid}`, { withCredentials: true });
      load();
    } catch (e) { toast.error('Error'); }
  };

  const deleteQuestion = async () => {
    if (!window.confirm(`¿Eliminar la duda "${q.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/questions/${question_id}`, { withCredentials: true });
      toast.success('Duda eliminada');
      navigate('/dudas');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const startEdit = () => {
    setEdit({
      category: q.category,
      levels: q.levels && q.levels.length > 0 ? q.levels : [q.level || 'A2'],
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      await axios.patch(
        `${API}/questions/${question_id}`,
        { category: edit.category, levels: edit.levels },
        { withCredentials: true }
      );
      toast.success('Duda movida');
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al mover');
    }
  };

  if (!q) return <div className="p-6 text-center text-[#78716C]">Cargando…</div>;
  const cat = catByVal[q.category] || CATEGORIES[0];
  const canResolve = user && (user.user_id === q.author_id || user.role === 'admin' || user.role === 'teacher');
  const canDelete = canResolve;
  const canMove = user && (user.role === 'admin' || user.role === 'teacher');
  const qLevels = q.levels && q.levels.length > 0 ? q.levels : [q.level];

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="duda-detail">
      <button onClick={() => navigate('/dudas')} className="text-sm text-[#57534E] hover:text-[#AB1C24] flex items-center gap-1 font-medium">
        <ArrowLeft size={14} /> Volver
      </button>

      {/* Question */}
      <article className="bg-white rounded-2xl border border-[#D6D3D1] p-5">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          {qLevels.map(lvl => <span key={lvl} className={`level-badge ${lvl}`}>{lvl}</span>)}
          <span className="text-xs font-semibold" style={{ color: cat.color }}>{cat.label}</span>
          {q.resolved ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5"><CheckCircle size={12} weight="fill" /> Resuelta</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-[#78716C]"><CircleDashed size={12} /> Abierta</span>
          )}
        </div>
        <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#292524]">{q.title}</h1>
        {q.content && <p className="text-[#44403C] mt-2 whitespace-pre-wrap">{q.content}</p>}
        <p className="text-xs text-[#A8A29E] mt-3">Por {q.author_name} · {new Date(q.created_at).toLocaleString('es-ES')}</p>

        {/* Teacher edit/move panel */}
        {canMove && editing && (
          <div className="mt-4 border border-dashed border-[#AB1C24]/40 rounded-xl p-4 bg-[#FDF0ED]/30 space-y-3" data-testid="duda-edit-panel">
            <div>
              <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wider mb-1.5">Mover a categoría</p>
              <Select value={edit.category} onValueChange={v => setEdit(p => ({ ...p, category: v }))}>
                <SelectTrigger className="w-full" data-testid="edit-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#57534E] uppercase tracking-wider mb-1.5">Niveles</p>
              <LevelPills value={edit.levels} onChange={v => setEdit(p => ({ ...p, levels: v }))} testIdPrefix="edit-level" />
            </div>
            <div className="flex gap-2">
              <Button onClick={saveEdit} className="bg-[#AB1C24] hover:bg-[#8A151C] gap-1.5" data-testid="edit-save-btn">
                <FloppyDisk size={14} weight="fill" /> Guardar
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)} className="gap-1.5" data-testid="edit-cancel-btn">
                <X size={14} /> Cancelar
              </Button>
            </div>
          </div>
        )}

        {(canResolve || canDelete || canMove) && !editing && (
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            {canResolve && (
              <button onClick={toggleResolved} className="text-xs font-semibold text-[#AB1C24] hover:underline" data-testid="duda-resolve-btn">
                {q.resolved ? 'Reabrir' : 'Marcar resuelta'}
              </button>
            )}
            {canMove && (
              <button
                onClick={startEdit}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#78716C] hover:text-[#292524] hover:underline"
                data-testid="duda-edit-btn"
              >
                <PencilSimpleLine size={12} weight="bold" /> Mover / editar
              </button>
            )}
            {canDelete && (
              <button
                onClick={deleteQuestion}
                className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 hover:underline"
                data-testid="duda-delete-btn"
              >
                <Trash size={12} weight="bold" /> Eliminar duda
              </button>
            )}
          </div>
        )}
      </article>

      {/* Answers */}
      <section className="space-y-3" data-testid="duda-answers">
        {(() => {
          const topLevel = q.answers.filter(a => !a.parent_answer_id);
          const repliesByParent = {};
          q.answers.forEach(a => {
            if (a.parent_answer_id) {
              (repliesByParent[a.parent_answer_id] = repliesByParent[a.parent_answer_id] || []).push(a);
            }
          });
          const countAll = q.answers.length;
          return (
            <>
              <h2 className="font-heading font-semibold text-lg text-[#292524]">
                {countAll} respuesta{countAll === 1 ? '' : 's'}
              </h2>
              {topLevel.map(a => (
                <AnswerItem
                  key={a.answer_id}
                  a={a}
                  replies={repliesByParent[a.answer_id] || []}
                  q={q}
                  user={user}
                  canResolve={canResolve}
                  replyToAnswer={replyToAnswer}
                  setReplyToAnswer={setReplyToAnswer}
                  replyAnswerText={replyAnswerText}
                  setReplyAnswerText={setReplyAnswerText}
                  submitReplyToAnswer={submitReplyToAnswer}
                  editingAnswer={editingAnswer}
                  setEditingAnswer={setEditingAnswer}
                  editAnswerText={editAnswerText}
                  setEditAnswerText={setEditAnswerText}
                  saveAnswerEdit={saveAnswerEdit}
                  deleteAnswer={deleteAnswer}
                  acceptAnswer={acceptAnswer}
                />
              ))}
              {countAll === 0 && <p className="text-sm text-[#78716C] italic">Aún no hay respuestas. ¡Anímate a responder!</p>}
            </>
          );
        })()}
      </section>

      {/* Reply form */}
      {user && (
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-4 space-y-3">
          <Textarea placeholder="Escribe tu respuesta…" value={reply} onChange={e => setReply(e.target.value)} rows={3} maxLength={4000} data-testid="reply-input" />
          <Button onClick={submit} disabled={sending || !reply.trim()} className="bg-[#AB1C24] hover:bg-[#8A151C] gap-2" data-testid="reply-submit">
            {sending ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="fill" />}
            Responder
          </Button>
        </div>
      )}
    </div>
  );
}

// ───── Single answer item with edit/reply/delete controls ─────
const AnswerItem = ({
  a, replies, q, user, canResolve,
  replyToAnswer, setReplyToAnswer, replyAnswerText, setReplyAnswerText, submitReplyToAnswer,
  editingAnswer, setEditingAnswer, editAnswerText, setEditAnswerText, saveAnswerEdit,
  deleteAnswer, acceptAnswer,
}) => {
  const isTeacher = a.author_role === 'admin' || a.author_role === 'teacher';
  const mine = user && user.user_id === a.author_id;
  const isAccepted = a.accepted || q.accepted_answer_id === a.answer_id;
  const canAccept = canResolve && !isAccepted && a.author_id !== q.author_id;
  const canMod = mine || user?.role === 'admin' || user?.role === 'teacher';
  const isEditing = editingAnswer === a.answer_id;
  const isReplying = replyToAnswer === a.answer_id;

  return (
    <div
      className={`rounded-xl border p-4 ${
        isAccepted
          ? 'border-emerald-500 bg-emerald-50/50 ring-1 ring-emerald-500/20'
          : isTeacher
            ? 'border-[#AB1C24] bg-[#FDF0ED]/40'
            : 'border-[#D6D3D1] bg-white'
      }`}
      data-testid={`answer-${a.answer_id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-[#292524] text-sm">{a.author_name}</span>
          {isTeacher && <span className="text-[10px] uppercase font-bold tracking-wider bg-[#AB1C24] text-white px-1.5 py-0.5 rounded">Profesor</span>}
          {isAccepted && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded" data-testid={`accepted-badge-${a.answer_id}`}>
              <Trophy size={10} weight="fill" /> Aceptada
            </span>
          )}
          {a.edited_at && <span className="text-[10px] text-[#A8A29E] italic">· editada</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#A8A29E]">{new Date(a.created_at).toLocaleString('es-ES')}</span>
          {canMod && !isEditing && (
            <>
              <button
                onClick={() => { setEditingAnswer(a.answer_id); setEditAnswerText(a.content || ''); }}
                className="p-1 rounded text-[#78716C] hover:text-[#292524] hover:bg-stone-100"
                aria-label="Editar"
                data-testid={`edit-answer-${a.answer_id}`}
              ><PencilSimpleLine size={14} /></button>
              <button
                onClick={() => deleteAnswer(a.answer_id)}
                className="p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50"
                aria-label="Eliminar"
                data-testid={`delete-answer-${a.answer_id}`}
              ><Trash size={14} /></button>
            </>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-2">
          <Textarea value={editAnswerText} onChange={e => setEditAnswerText(e.target.value)} rows={3} data-testid={`edit-answer-input-${a.answer_id}`} />
          <div className="flex gap-2">
            <Button size="sm" className="bg-[#AB1C24] hover:bg-[#8A151C] gap-1" onClick={() => saveAnswerEdit(a.answer_id)} data-testid={`save-answer-${a.answer_id}`}>
              <FloppyDisk size={12} weight="fill" /> Guardar
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditingAnswer(null)}>Cancelar</Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[#44403C] whitespace-pre-wrap">{a.content}</p>
      )}

      {!isEditing && (
        <div className="flex items-center gap-3 mt-3 flex-wrap">
          {user && (
            <button
              onClick={() => { setReplyToAnswer(isReplying ? null : a.answer_id); setReplyAnswerText(''); }}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#57534E] hover:text-[#AB1C24]"
              data-testid={`reply-to-answer-btn-${a.answer_id}`}
            >
              <ArrowBendUpLeft size={12} weight="bold" /> Responder
            </button>
          )}
          {canAccept && (
            <button
              onClick={() => acceptAnswer(a.answer_id)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-full px-3 py-1"
              data-testid={`accept-answer-${a.answer_id}`}
            >
              <CheckCircle size={14} weight="fill" /> Marcar como aceptada (+10 pts)
            </button>
          )}
        </div>
      )}

      {/* Inline reply form */}
      {isReplying && (
        <div className="mt-3 ml-2 pl-3 border-l-2 border-[#AB1C24]/40 space-y-2">
          <Textarea
            value={replyAnswerText}
            onChange={e => setReplyAnswerText(e.target.value)}
            placeholder={user?.role === 'admin' || user?.role === 'teacher' ? 'Tu respuesta como profesor…' : 'Tu respuesta…'}
            rows={2}
            data-testid={`reply-answer-input-${a.answer_id}`}
          />
          <div className="flex gap-2">
            <Button size="sm" className="bg-[#AB1C24] hover:bg-[#8A151C] gap-1" onClick={() => submitReplyToAnswer(a.answer_id)} data-testid={`submit-reply-answer-${a.answer_id}`}>
              <PaperPlaneTilt size={12} weight="fill" /> Enviar
            </Button>
            <Button size="sm" variant="outline" onClick={() => setReplyToAnswer(null)}>Cancelar</Button>
          </div>
        </div>
      )}

      {/* Nested replies (rendered via separate component to avoid JSX self-recursion) */}
      {replies && replies.length > 0 && (
        <div className="mt-3 ml-2 pl-3 border-l-2 border-stone-200 space-y-2" data-testid={`replies-${a.answer_id}`}>
          {replies.map(r => (
            <ReplyItem
              key={r.answer_id}
              r={r}
              user={user}
              editingAnswer={editingAnswer}
              setEditingAnswer={setEditingAnswer}
              editAnswerText={editAnswerText}
              setEditAnswerText={setEditAnswerText}
              saveAnswerEdit={saveAnswerEdit}
              deleteAnswer={deleteAnswer}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Nested-reply item (one level deep, no self-recursion — prevents Babel JSX stack overflow)
const ReplyItem = ({ r, user, editingAnswer, setEditingAnswer, editAnswerText, setEditAnswerText, saveAnswerEdit, deleteAnswer }) => {
  const isReplyTeacher = r.author_role === 'admin' || r.author_role === 'teacher';
  const mine = user && user.user_id === r.author_id;
  const canMod = mine || user?.role === 'admin' || user?.role === 'teacher';
  const isEditing = editingAnswer === r.answer_id;
  return (
    <div className={`rounded-lg border p-3 ${isReplyTeacher ? 'border-[#AB1C24]/50 bg-[#FDF0ED]/30' : 'border-stone-200 bg-white'}`} data-testid={`answer-${r.answer_id}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-[#292524] text-xs">{r.author_name}</span>
          {isReplyTeacher && <span className="text-[9px] uppercase font-bold tracking-wider bg-[#AB1C24] text-white px-1 py-0.5 rounded">Profesor</span>}
          {r.edited_at && <span className="text-[9px] text-[#A8A29E] italic">· editada</span>}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-[#A8A29E]">{new Date(r.created_at).toLocaleString('es-ES')}</span>
          {canMod && !isEditing && (
            <>
              <button onClick={() => { setEditingAnswer(r.answer_id); setEditAnswerText(r.content || ''); }} className="p-1 text-[#78716C] hover:text-[#292524]" data-testid={`edit-answer-${r.answer_id}`}><PencilSimpleLine size={12} /></button>
              <button onClick={() => deleteAnswer(r.answer_id)} className="p-1 text-red-400 hover:text-red-600" data-testid={`delete-answer-${r.answer_id}`}><Trash size={12} /></button>
            </>
          )}
        </div>
      </div>
      {isEditing ? (
        <div className="space-y-1">
          <Textarea value={editAnswerText} onChange={e => setEditAnswerText(e.target.value)} rows={2} data-testid={`edit-answer-input-${r.answer_id}`} />
          <div className="flex gap-1">
            <Button size="sm" className="bg-[#AB1C24] hover:bg-[#8A151C] gap-1 h-7 text-xs" onClick={() => saveAnswerEdit(r.answer_id)} data-testid={`save-answer-${r.answer_id}`}>
              <FloppyDisk size={10} weight="fill" /> OK
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingAnswer(null)}>Abbr.</Button>
          </div>
        </div>
      ) : (
        <p className="text-sm text-[#44403C] whitespace-pre-wrap">{r.content}</p>
      )}
    </div>
  );
};

